import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import {
  Theme,
  css,
  styled as muiStyled,
  useTheme,
} from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isInBrowser } from '~/constants';
import * as GQL from '~/graphql/generated/client';
import Arranger from '~c/components/base/arranger';
import { getDesignTokens } from '~c/components/base/core';
import LogoSelector from '~c/components/base/icon/logo-selector';
import { useSnackbarContext } from '~c/components/base/snackbar';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import { takeOAuthReturn } from '~c/helpers/pending-oauth-return';
import {
  COMPLIANCE_DASHBOARD_PATH,
  useDefaultDashboardRoute,
} from '~c/hooks/use-default-dashboard-route';
import { useDesignTokens } from '~c/hooks/use-design-tokens';
import { useUserProfile } from '~c/hooks/user-profile';
import { nodeAuthApi, userProfileApi } from '~c/services';

interface OrganizationCardProps {
  theme: Theme;
  selected?: boolean;
}

interface OrganizationData {
  _id: string;
  name: string;
  branding?: {
    logoUrl?: string | null;
  } | null;
}

const OrganizationCard = muiStyled(Card)<{ selected?: boolean }>(
  ({ theme, selected }: OrganizationCardProps) => ({
    height: '100%',
    transition: 'all 0.2s ease-in-out',
    border: selected
      ? `2px solid ${theme.palette.primary.main}`
      : `1px solid ${theme.palette.divider}`,
    backgroundColor: selected
      ? `${theme.palette.primary.main}0D`
      : theme.palette.background.paper,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
      borderColor: theme.palette.primary.main,
    },
  })
);

const OrganizationAvatar = muiStyled(Avatar)(({ theme }) => {
  const t = getDesignTokens(
    (theme?.palette?.mode ?? 'light') as 'light' | 'dark'
  );
  return {
    width: 56,
    height: 56,
    backgroundColor: t.avatarBg,
    color: t.avatarText,
    fontSize: '20px',
    fontWeight: 600,
    margin: '0 auto',
  };
});

// Shared styles
const pageContainerSx = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 4,
  px: 2,
} as const;

const narrowContentSx = {
  maxWidth: 500,
  width: '100%',
  textAlign: 'center',
} as const;

const pageTitleSx = {
  fontWeight: 600,
  mb: spc.xs,
} as const;

const subtitleSx = {
  color: 'text.secondary',
  mb: spc.xl,
  textAlign: 'center' as const,
};

// Built dynamically in component via useDesignTokens for theme awareness

interface Props {
  className?: string;
}

const getOrganizationInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Wrapper to prevent SSR - component requires browser environment
const OrganizationSelectorWrapper: React.FC<Props> = (props) => {
  if (!isInBrowser()) {
    return null;
  }
  return <OrganizationSelectorImpl {...props} />;
};

const OrganizationSelectorImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const defaultRoute = useDefaultDashboardRoute();
  // A pending assistant sign-in is a full page load: its consent page must read the newly selected organization.
  const goHome = () => {
    const pending = takeOAuthReturn();
    if (pending) {
      window.location.assign(pending);
      return;
    }
    navigate(defaultRoute ?? COMPLIANCE_DASHBOARD_PATH);
  };
  const Snackbar = useSnackbarContext();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === 'dark';
  const t = useDesignTokens();

  const primaryButtonSx = React.useMemo(
    () => ({
      backgroundColor: t.buttonPrimary,
      color: t.buttonPrimaryText,
      '&:hover': {
        backgroundColor: t.buttonPrimaryHover,
        opacity: 0.9,
      },
    }),
    [t]
  );

  const outlinedButtonSx = React.useMemo(
    () => ({
      borderColor: t.accent,
      color: t.accent,
      '&:hover': {
        borderColor: t.accent,
        backgroundColor: t.accentBg,
      },
    }),
    [t]
  );

  const defaultChipSx = React.useMemo(
    () => ({
      position: 'absolute' as const,
      top: 12,
      left: 12,
      backgroundColor: t.badgeAccent,
      color: t.badgeAccentContrast,
      fontSize: '10px',
      height: '20px',
    }),
    [t]
  );
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get current user auth_uid
  const userResult = nodeAuthApi.useCurrentUserQuery();
  const { userProfile } = useUserProfile();

  const { refetch: refetchUserProfile, isLoading: isProfileLoading } =
    userProfileApi.useGetProfileQuery(userResult.data?.auth_uid ?? '', {
      skip: userResult.data?.auth_uid == null,
    });

  const {
    data: orgData,
    loading: orgLoading,
    error: orgError,
    refetch: refetchOrgs,
  } = GQL.useGetUserOrganizationsQuery({
    skip: !userResult.data?.auth_uid,
    fetchPolicy: 'network-only',
  });

  // Check if we're still loading user data or organization data.
  const isLoadingData = userResult.isLoading || isProfileLoading || orgLoading;

  const [switchContext] = GQL.useSwitchOrganizationContextMutation({
    onCompleted: async (data) => {
      if (data.switchOrganizationContext.success) {
        await refetchUserProfile().catch(() => {});
        goHome();
      } else {
        Snackbar.open(data.switchOrganizationContext.message, { showOk: true });
        setIsLoading(false);
      }
    },
    onError: (error) => {
      Snackbar.open(error.message, { showOk: true });
      setIsLoading(false);
    },
  });

  const defaultOrgId = userProfile?.defaultOrganizationId;

  // Set default organization as selected on mount
  useEffect(() => {
    if (defaultOrgId) {
      setSelectedOrgId(defaultOrgId);
    }
  }, [defaultOrgId]);

  // Track whether the single-org auto-switch has been initiated to prevent
  // the mutation from firing multiple times as dependencies change.
  // useRef instead of useState to avoid re-render race conditions.
  const hasAutoSwitchedRef = useRef(false);

  // Handle single org auto-redirect and zero org redirect
  useEffect(() => {
    if (hasAutoSwitchedRef.current) {
      return;
    }

    if (!isLoadingData && orgData?.userOrganizationContexts?.length === 1) {
      const singleOrgId =
        orgData.userOrganizationContexts[0]?.organization?._id;
      if (singleOrgId) {
        localStorage.setItem('currentOrgId', singleOrgId);

        if (defaultOrgId === singleOrgId) {
          goHome();
        } else {
          hasAutoSwitchedRef.current = true;
          switchContext({
            variables: { input: { organizationId: singleOrgId } },
          });
        }
      }
    }
  }, [orgData, isLoadingData, navigate, defaultOrgId, switchContext]);

  const handleOrganizationSelect = (orgId: string) => {
    setSelectedOrgId(orgId);
  };

  const handleContinue = async () => {
    if (!selectedOrgId) {
      Snackbar.open('Please select an organization', { showOk: true });
      return;
    }

    localStorage.setItem('currentOrgId', selectedOrgId);

    // Same org selected - navigate directly
    if (selectedOrgId === defaultOrgId) {
      goHome();
      return;
    }

    // Different org - call mutation (navigates on success)
    setIsLoading(true);
    await switchContext({
      variables: { input: { organizationId: selectedOrgId } },
    });
  };

  // Filter and extract valid organizations
  const organizations: OrganizationData[] =
    orgData?.userOrganizationContexts
      ?.filter((m) => m?.organization?._id && m?.organization?.name)
      ?.map((m) => m.organization as OrganizationData) ?? [];

  // Show loader while loading or auto-redirecting single org users
  if (isLoadingData || organizations.length === 1) {
    return (
      <Box
        className={className}
        sx={{ ...pageContainerSx, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress size={48} />
        <Text sz="body14" sx={{ color: 'text.secondary', mt: 2 }}>
          Loading your organizations...
        </Text>
      </Box>
    );
  }

  // Show error state when organization fetch fails
  if (orgError) {
    return (
      <Box className={className} sx={pageContainerSx}>
        <Box sx={narrowContentSx}>
          <div className="logo-container">
            <LogoSelector prefersDarkMode={prefersDarkMode} />
          </div>
          <Spacer mb={spc.xs} />
          <Typography variant="h5" sx={pageTitleSx}>
            Unable to Load Organizations
          </Typography>
          <Text sz="body14" sx={subtitleSx}>
            We couldn&apos;t fetch your organizations. Please try again or
            contact support if the issue persists.
          </Text>
          <Button
            variant="contained"
            onClick={() => refetchOrgs()}
            sx={primaryButtonSx}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  // Show empty state when user has no active organization memberships
  if (!orgLoading && organizations.length === 0) {
    return (
      <Box className={className} sx={pageContainerSx}>
        <Box sx={narrowContentSx}>
          <div className="logo-container">
            <LogoSelector prefersDarkMode={prefersDarkMode} />
          </div>
          <Spacer mb={spc.xs} />
          <Typography variant="h5" sx={pageTitleSx}>
            No Active Memberships
          </Typography>
          <Text sz="body14" sx={subtitleSx}>
            You don&apos;t have any active organization memberships. Please
            contact your organization administrator to request access.
          </Text>
          <Button
            variant="outlined"
            onClick={() => navigate('/sign-in')}
            sx={outlinedButtonSx}
          >
            Back to Sign In
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={className} sx={pageContainerSx}>
      <Box sx={{ maxWidth: 1000, width: '100%' }}>
        <Arranger direction="column" alignItems="center">
          <div className="logo-container">
            <LogoSelector prefersDarkMode={prefersDarkMode} />
          </div>
          <Spacer mb={spc.xs} />
          <Typography variant="h5" sx={pageTitleSx}>
            Select Your Organization
          </Typography>
          <Text sz="body14" sx={subtitleSx}>
            Choose the organization you want to work with
          </Text>
        </Arranger>

        <Grid
          container
          spacing={3}
          sx={{ mb: spc.xl, justifyContent: 'center' }}
        >
          {organizations.map((org) => {
            const isSelected = selectedOrgId === org._id;
            const isDefault = userProfile?.defaultOrganizationId === org._id;

            return (
              <Grid item xs={12} sm={6} md={4} key={org._id}>
                <OrganizationCard selected={isSelected}>
                  <CardActionArea
                    onClick={() => handleOrganizationSelect(org._id)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      {isSelected && (
                        <CheckCircleIcon
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            color: 'primary.main',
                            fontSize: 28,
                          }}
                        />
                      )}

                      {isDefault && (
                        <Chip label="Default" size="small" sx={defaultChipSx} />
                      )}

                      <OrganizationAvatar alt={org.name}>
                        {getOrganizationInitials(org.name)}
                      </OrganizationAvatar>

                      <Typography
                        variant="h6"
                        sx={{
                          mt: 2,
                          mb: 0.5,
                          fontWeight: 600,
                          color: 'primary.main',
                          fontSize: '16px',
                        }}
                      >
                        {org.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </OrganizationCard>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={!selectedOrgId || isLoading}
            sx={{ minWidth: 200, ...primaryButtonSx }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const OrganizationSelector = muiStyled(OrganizationSelectorWrapper)(
  () => css`
    .logo-container {
      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default OrganizationSelector;
